import {
  classMap,
  UxOrientationEnum,
  UxVisualActionContainer,
  VisualActionProps
} from "@imaginelearning/mps-ux-component-library";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Row } from "react-bootstrap";
import useGradeRange from "hooks/usegraderange";
import useBreakpoint from "../../liftoff/hooks/useBreakpoint";
import SmallScreenPanel from "../../small-screen-panel/small-screen-panel";

export interface BaseCourseMapProps {
  assignments?: VisualActionProps[];
  error: string;
}

const intersectionObserverOptions = {
  root: null,
  rootMargin: "150px",
  threshold: 0
};

function BaseCourseMap({ assignments, error }: BaseCourseMapProps) {
  const focusRef = useRef<HTMLDivElement>();
  const { is6to12 } = useGradeRange();
  const { isSmallScreen } = useBreakpoint();
  const [chevronUp, setChevronUp] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showBackToCurrent, setShowBackToCurrent] = useState(false);

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio === 0) {
        setChevronUp(entry.boundingClientRect.y < 0);
        setShowBackToCurrent(true);
      } else {
        setShowBackToCurrent(false);
      }
    });
  }, intersectionObserverOptions);

  const setVisualActionFocus = useCallback(() => {
    if (focusRef?.current) {
      observer.observe(focusRef.current);
      const visualActionBody = document.getElementById("abld-assignment-lessons-container") as HTMLDivElement;
      const goButtons = focusRef.current.getElementsByTagName("button");
      if (visualActionBody === null) {
        return;
      }
      visualActionBody.scrollTo(0, focusRef.current.offsetTop - window.innerHeight / 3);
      goButtons[0]?.focus();
      setFocused(true);
    }
  }, [focusRef, observer]);

  useLayoutEffect(() => {
    if (!focused) {
      setVisualActionFocus();
    }
  }, [focusRef, focused, setVisualActionFocus]);

  return error !== "" ? (
    <Row>
      <span className="text-danger" data-testid="base-course-map-error">
        {error}
      </span>
    </Row>
  ) : (
    <>
      <div className="abld-assignment-lessons-list-row">
        <UxVisualActionContainer
          items={assignments}
          className={classMap(
            "w-100 text-center course-map abld-assignment-lessons-list",
            is6to12 ? "align-items-center overflow-hidden px-5" : "course-map-limited"
          )}
          orientation={UxOrientationEnum.VERTICAL}
          staggered={!is6to12}
          focusRef={focusRef}
          showBackToCurrent={showBackToCurrent && !isSmallScreen}
          chevronUp={chevronUp}
          callback={setVisualActionFocus}
          colCount={1}
        />
      </div>
      {showBackToCurrent && isSmallScreen && (
        <SmallScreenPanel
          isFooter={true}
          onBackToCurrentClick={() => {
            setVisualActionFocus();
          }}
        />
      )}
    </>
  );
}

export default BaseCourseMap;
